/**
 * components/analytics/AnalyticsPanel.tsx
 * Scan log viewer — every gapper from every scan, with full per-ticker
 * detail (same score breakdown the Scanner tab shows, plus the concrete
 * technical reason behind any skipped factor) for algorithm tuning.
 * Lives on its own full-width "Journal & Analytics" tab now, so this is a
 * real data table instead of the old cramped single-column sidebar list.
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import { Trash2, RefreshCw, TrendingUp, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { analyticsApi } from '../../lib/api';
import { ScoreBreakdownCard } from '../shared/ScoreBreakdownCard';
import type { OptionSnapshot } from '../shared/ScoreBreakdownCard';
import type { ScanRecord, ScanSummary, ScoreFactor, DiagEntry, ScoreBreakdown } from '../../types';

function fmt(v: number | null | undefined, d = 2): string {
  if (v == null || isNaN(v)) return '—';
  return v.toFixed(d);
}

function parseJsonArray<T>(raw: string | null | undefined): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    return [];
  }
}

/** Reconstruct a ScoreBreakdown from a persisted scan row, for ScoreBreakdownCard. */
function breakdownFromRecord(r: ScanRecord): ScoreBreakdown {
  const factors = parseJsonArray<ScoreFactor>(r.score_factors);
  const evaluatedFactors = factors.filter(f => f.status !== 'skipped').length;
  return {
    total: r.score,
    verdict: r.score >= 70 ? 'high-probability' : r.score >= 50 ? 'moderate' : 'low-probability',
    recommendation: r.score >= 70 ? 'enter-full' : r.score >= 50 ? 'enter-half' : 'skip',
    factors,
    evaluatedFactors,
    totalFactors: 13,
  };
}

const SCORE_CLS = (s: number) =>
  s >= 70 ? 'text-bull bg-bull/10 border-bull/20'
  : s >= 50 ? 'text-amber bg-amber/10 border-amber/20'
  : 'text-bear bg-bear/10 border-bear/20';

const CAT_CLS: Record<string, string> = {
  earnings: 'text-bull',
  fda:      'text-violet',
  upgrade:  'text-sky',
  contract: 'text-sky',
  macro:    'text-amber',
  unknown:  'text-ink-3',
};

const OPT_CLS: Record<string, string> = {
  pass:      'text-bull',
  fail:      'text-bear',
  unchecked: 'text-ink-3',
};

// ── Expanded row detail ─────────────────────────────────────────

function RecordDetail({ r }: { r: ScanRecord }) {
  const diagnostics = parseJsonArray<DiagEntry>(r.diagnostics);
  const breakdown   = breakdownFromRecord(r);
  const optionInfo: OptionSnapshot = {
    optionSymbol: r.option_symbol,
    strike:       r.strike,
    expiration:   r.expiration,
    bid:          r.bid_val,
    ask:          r.ask_val,
    mid:          r.mid_val,
    spread:       r.spread_val,
    openInterest: r.oi_val,
    delta:        r.delta_val,
    status:       r.options_status,
  };

  return (
    <div className="p-4 bg-bg-2/30 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <ScoreBreakdownCard score={r.score} scoreBreakdown={breakdown} optionInfo={optionInfo} />
      </div>
      <div className="space-y-3">
        {r.catalyst_summary && (
          <div className="card">
            <div className="section-label mb-2">Catalyst</div>
            <div className="text-xs text-ink-2 leading-relaxed">{r.catalyst_summary}</div>
          </div>
        )}

        <div className="card">
          <div className="section-label mb-2 flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-amber" />
            Diagnostics — why factors were skipped
          </div>
          {diagnostics.length === 0 ? (
            <div className="text-2xs text-ink-3">
              No issues recorded — every factor either evaluated cleanly or this scan predates diagnostic logging.
            </div>
          ) : (
            <div className="space-y-1.5">
              {diagnostics.map((d, i) => (
                <div key={i} className="text-2xs font-mono leading-relaxed">
                  <span className={clsx('px-1 py-0.5 rounded border mr-1.5',
                    d.level === 'error' ? 'text-bear border-bear/30 bg-bear/10' : 'text-amber border-amber/30 bg-amber/10')}>
                    {d.stage}
                  </span>
                  <span className="text-ink-2">{d.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card grid grid-cols-3 gap-3 text-2xs font-mono">
          <div><span className="text-ink-3">Price</span><br /><span className="text-ink-1">${fmt(r.price)}</span></div>
          <div><span className="text-ink-3">Prev close</span><br /><span className="text-ink-1">${fmt(r.prev_close)}</span></div>
          <div><span className="text-ink-3">SPY</span><br />
            <span className={r.spy_change_pct != null && r.spy_change_pct > 0 ? 'text-bull' : 'text-bear'}>{fmt(r.spy_change_pct, 2)}%</span>
          </div>
        </div>

        {r.actual_move_pct != null && (
          <div className="text-2xs">
            <span className="text-ink-3">Actual move: </span>
            <span className={r.actual_move_pct > 0 ? 'text-bull' : 'text-bear'}>
              {r.actual_move_pct > 0 ? '+' : ''}{fmt(r.actual_move_pct, 1)}%
            </span>
          </div>
        )}
        {r.notes && <div className="text-2xs text-ink-3 italic">{r.notes}</div>}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────

export function AnalyticsPanel() {
  const [view, setView]             = useState<'log' | 'summary' | 'outcomes'>('log');
  const [records, setRecords]       = useState<ScanRecord[]>([]);
  const [summaries, setSummaries]   = useState<ScanSummary[]>([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [filterDate,     setFilterDate]     = useState('');
  const [filterSym,      setFilterSym]      = useState('');
  const [filterMinScore, setFilterMinScore] = useState('');
  const [filterCatalyst, setFilterCatalyst] = useState('');
  const [filterSource,   setFilterSource]   = useState<'' | 'manual' | 'auto'>('');
  const [offset, setOffset]                 = useState(0);
  const LIMIT = 30;

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await analyticsApi.scans({
        limit: LIMIT, offset,
        date: filterDate || undefined,
        sym:  filterSym  || undefined,
        minScore: filterMinScore ? parseInt(filterMinScore) : undefined,
        catalyst: filterCatalyst || undefined,
        source: filterSource || undefined,
      });
      setRecords(data.rows);
      setTotal(data.total);
    } catch {
      setError('Failed to load scan log');
    } finally {
      setLoading(false);
    }
  }, [offset, filterDate, filterSym, filterMinScore, filterCatalyst, filterSource]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummaries(await analyticsApi.summary());
    } catch {
      setError('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'log')     fetchLog();
    if (view === 'summary') fetchSummary();
  }, [view, fetchLog, fetchSummary]);

  async function deleteRecord(id: string) {
    if (!confirm('Delete this scan record?')) return;
    await analyticsApi.delete(id);
    setRecords(r => r.filter(x => x.id !== id));
    setTotal(t => t - 1);
  }

  async function deleteDate(date: string) {
    if (!confirm(`Delete all scan records for ${date}?`)) return;
    await analyticsApi.deleteByDate(date);
    fetchSummary();
    if (filterDate === date) fetchLog();
    else setSummaries(s => s.filter(x => x.scan_date !== date));
  }

  async function deleteOld(days: number) {
    if (!confirm(`Delete all records older than ${days} days?`)) return;
    await analyticsApi.deleteOlderThan(days);
    fetchLog();
    fetchSummary();
  }

  async function updateOutcome(id: string, outcome: string) {
    await analyticsApi.update(id, { outcome });
    setRecords(rs => rs.map(r => r.id === id ? { ...r, outcome } : r));
  }

  const ViewTabs = () => (
    <div className="flex gap-1">
      {(['log', 'summary', 'outcomes'] as const).map(v => (
        <button key={v} onClick={() => setView(v)}
          className={clsx('text-2xs font-mono px-2.5 py-1 rounded border transition-colors',
            view === v
              ? 'bg-bull/10 border-bull/30 text-bull'
              : 'border-border text-ink-3 hover:text-ink-1')}>
          {v === 'log' ? 'Log' : v === 'summary' ? 'By Date' : 'Outcomes'}
        </button>
      ))}
    </div>
  );

  // ── View: Log ───────────────────────────────────────────────
  if (view === 'log') return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Toolbar */}
      <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-3 border-b border-border space-y-2 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <ViewTabs />
          <div className="flex items-center gap-3">
            <span className="text-2xs text-ink-3 font-mono">
              {total} records {filterDate && `· ${filterDate}`}
            </span>
            <button onClick={fetchLog} className="text-ink-3 hover:text-ink-1 p-1 rounded hover:bg-bg-3">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => deleteOld(30)}
              className="text-2xs font-mono text-bear/60 hover:text-bear px-1.5 py-1 rounded hover:bg-bear/10 border border-transparent hover:border-bear/20">
              Clear 30d+
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <input type="date" value={filterDate}
            onChange={e => { setFilterDate(e.target.value); setOffset(0); }}
            className="input text-2xs py-1 w-40" />
          <input placeholder="Symbol…" value={filterSym}
            onChange={e => { setFilterSym(e.target.value); setOffset(0); }}
            className="input text-2xs py-1 uppercase w-28" />
          <input type="number" placeholder="Min score…" value={filterMinScore}
            onChange={e => { setFilterMinScore(e.target.value); setOffset(0); }}
            className="input text-2xs py-1 w-28" />
          <select value={filterCatalyst}
            onChange={e => { setFilterCatalyst(e.target.value); setOffset(0); }}
            className="input text-2xs py-1 w-36">
            <option value="">All catalysts</option>
            <option value="earnings">Earnings</option>
            <option value="fda">FDA</option>
            <option value="upgrade">Upgrade</option>
            <option value="contract">Contract</option>
            <option value="unknown">Unknown</option>
          </select>
          <select value={filterSource}
            onChange={e => { setFilterSource(e.target.value as typeof filterSource); setOffset(0); }}
            className="input text-2xs py-1 w-32">
            <option value="">Manual + Auto</option>
            <option value="manual">Manual only</option>
            <option value="auto">Auto only</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {error && <div className="p-3 text-2xs text-bear">{error}</div>}
        {loading && records.length === 0 && (
          <div className="p-4 text-center text-ink-3 text-xs">Loading…</div>
        )}
        {!loading && records.length === 0 && !error && (
          <div className="p-8 text-center text-ink-3 text-xs">No scan records yet — run a scan to start logging</div>
        )}

        {records.length > 0 && (
          <table className="w-full min-w-[900px] text-2xs font-mono border-collapse">
            <thead className="sticky top-0 bg-bg-1 border-b border-border text-ink-3">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Src</th>
                <th className="px-3 py-2 font-medium">Sym</th>
                <th className="px-3 py-2 font-medium">Score</th>
                <th className="px-3 py-2 font-medium">Catalyst</th>
                <th className="px-3 py-2 font-medium">Gap</th>
                <th className="px-3 py-2 font-medium">RVOL</th>
                <th className="px-3 py-2 font-medium">RSI</th>
                <th className="px-3 py-2 font-medium">MACD</th>
                <th className="px-3 py-2 font-medium">VWAP</th>
                <th className="px-3 py-2 font-medium">Options</th>
                <th className="px-3 py-2 font-medium">Outcome</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <Fragment key={r.id}>
                  <tr
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                    className="border-b border-border/40 hover:bg-bg-2/50 cursor-pointer">
                    <td className="px-3 py-2 text-ink-3 whitespace-nowrap">{r.scan_date}</td>
                    <td className="px-3 py-2 text-ink-3 whitespace-nowrap">{r.scan_time}</td>
                    <td className="px-3 py-2">
                      <span className={clsx('badge', r.source === 'auto' ? 'badge-violet' : 'badge-sky')}>
                        {r.source === 'auto' ? 'Auto' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-ink-1">{r.sym}</td>
                    <td className="px-3 py-2">
                      <span className={clsx('px-1.5 py-0.5 rounded border', SCORE_CLS(r.score))}>{r.score}</span>
                    </td>
                    <td className={clsx('px-3 py-2', CAT_CLS[r.catalyst_type] ?? 'text-ink-3')}>
                      {r.catalyst_type?.toUpperCase()}
                    </td>
                    <td className="px-3 py-2 text-bull">+{fmt(r.change_pct, 1)}%</td>
                    <td className="px-3 py-2 text-ink-2">{r.rvol}×</td>
                    <td className="px-3 py-2 text-ink-2">{fmt(r.rsi14, 1)}</td>
                    <td className={clsx('px-3 py-2', r.macd_histogram != null && r.macd_histogram > 0 ? 'text-bull' : 'text-bear')}>
                      {fmt(r.macd_histogram, 4)}
                    </td>
                    <td className={clsx('px-3 py-2', r.above_vwap == null ? 'text-ink-3' : r.above_vwap ? 'text-bull' : 'text-bear')}>
                      {r.above_vwap == null ? '—' : r.above_vwap ? 'Above' : 'Below'}
                    </td>
                    <td className={clsx('px-3 py-2', OPT_CLS[r.options_status] ?? 'text-ink-3')}>
                      {r.options_status === 'pass' ? 'Opt✓' : r.options_status === 'fail' ? 'Opt✗' : '—'}
                    </td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <select value={r.outcome ?? ''}
                        onChange={e => updateOutcome(r.id, e.target.value)}
                        className={clsx('text-2xs font-mono py-0.5 px-1 rounded border bg-bg-2',
                          r.outcome === 'win'   ? 'text-bull border-bull/30'
                          : r.outcome === 'loss' ? 'text-bear border-bear/30'
                          : r.outcome === 'open'      ? 'text-sky border-sky/30'
                          : r.outcome === 'cancelled' ? 'text-amber border-amber/30'
                          : r.outcome === 'breakeven' ? 'text-ink-2 border-border-strong'
                          : 'text-ink-3 border-border')}>
                        <option value="">outcome?</option>
                        <option value="win">Win</option>
                        <option value="loss">Loss</option>
                        <option value="breakeven">Breakeven</option>
                        <option value="open">Open</option>
                        {/* Order placed and accepted by the broker, but never filled — timed
                            out or rejected/expired. Auto-set by the trade lifecycle itself;
                            see reconciliation.service.ts. Distinct from "Skipped," which is
                            a candidate that was never even attempted. */}
                        <option value="cancelled">Cancelled — unfilled</option>
                        <option value="skip">Skipped</option>
                        <option value="no-trade">No trade</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {expandedId === r.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        <button onClick={e => { e.stopPropagation(); deleteRecord(r.id); }}
                          className="text-ink-3 hover:text-bear">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr>
                      <td colSpan={14} className="p-0">
                        <RecordDetail r={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <button disabled={offset === 0}
              onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
              className="text-2xs font-mono text-ink-3 hover:text-ink-1 disabled:opacity-30 px-2 py-1 border border-border rounded">
              ← Prev
            </button>
            <span className="text-2xs text-ink-3 font-mono">
              {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
            </span>
            <button disabled={offset + LIMIT >= total}
              onClick={() => setOffset(o => o + LIMIT)}
              className="text-2xs font-mono text-ink-3 hover:text-ink-1 disabled:opacity-30 px-2 py-1 border border-border rounded">
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── View: Summary by date ────────────────────────────────────
  if (view === 'summary') return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-border flex items-center justify-between flex-shrink-0">
        <ViewTabs />
        <button onClick={fetchSummary}><RefreshCw size={12} className={clsx('text-ink-3', loading && 'animate-spin')} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 content-start">
        {summaries.map(s => (
          <div key={s.scan_date} className="bg-bg-2 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium">{s.scan_date}</span>
              <button onClick={() => deleteDate(s.scan_date)} className="text-ink-3 hover:text-bear p-0.5">
                <Trash2 size={11} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-2xs font-mono">
              <div><span className="text-ink-3">Gappers</span><br/>
                <span className="text-ink-1 font-medium">{s.total_gappers}</span></div>
              <div><span className="text-ink-3">Avg score</span><br/>
                <span className={clsx('font-medium', s.avg_score >= 70 ? 'text-bull' : s.avg_score >= 50 ? 'text-amber' : 'text-bear')}>{s.avg_score}</span></div>
              <div><span className="text-ink-3">High prob</span><br/>
                <span className="text-bull font-medium">{s.high_prob_count}</span></div>
              <div><span className="text-ink-3">Catalysts</span><br/>
                <span className="text-ink-1">{s.catalyst_count}</span></div>
              <div><span className="text-ink-3">Opt pass</span><br/>
                <span className="text-bull">{s.options_pass_count}</span></div>
              <div><span className="text-ink-3">W / L</span><br/>
                <span className="text-bull">{s.wins}</span>
                <span className="text-ink-3"> / </span>
                <span className="text-bear">{s.losses}</span></div>
            </div>
            <div className="mt-2 text-2xs text-ink-3 font-mono">First scan: {s.first_scan} ET</div>
          </div>
        ))}
        {summaries.length === 0 && !loading && (
          <div className="text-2xs text-ink-3 text-center py-8 col-span-full">
            No scan data yet — run a scan to start logging
          </div>
        )}
      </div>
    </div>
  );

  // ── View: Outcomes ───────────────────────────────────────────
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-border flex-shrink-0 space-y-2">
        <ViewTabs />
        <p className="text-2xs text-ink-3">
          Win/loss breakdown by score bracket. Set outcomes in the Log tab to populate this.
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="text-2xs text-ink-3 text-center py-8">
          <TrendingUp size={24} className="mx-auto mb-2 opacity-30" />
          Mark trade outcomes in the Log tab (Win / Loss / Skipped) to see performance by score bracket here.
        </div>
      </div>
    </div>
  );
}
